import moment from 'moment-timezone'
import { getTime } from 'date-fns'
import { cloneDeep } from 'lodash'

import API from '../api/api'
import { DEFAULT_ANALYTICS_PERIOD_VALUE } from '../components/BuildingAnalytics/AnalyticsConstants'

const SET_BUILDING_STATISTICS = 'SET_BUILDING_STATISTICS'
const SET_LOADING_ANALYTICS_DATA = 'SET_LOADING_ANALYTICS_DATA'
const SET_TYPE = 'SET_TYPE'
const SET_DATE = 'SET_DATE'
const SET_CHART = 'SET_CHART'
const SET_ANALYTICS_DATA = 'SET_ANALYTICS_DATA'
const SET_REVIEWS_DATA = 'SET_REVIEWS_DATA'
const TOGGLE_BUILDING_FILTERS = 'TOGGLE_BUILDING_FILTERS'
const SELECT_ALL_BUILDINGS = 'SELECT_ALL_BUILDINGS'
const SET_PACKAGES_ANALYTICS = 'SET_PACKAGES_ANALYTICS'
const SET_LOADING_PACKAGES_ANALYTICS = 'SET_LOADING_PACKAGES_ANALYTICS'

const initialState = {
  buildingStatistics: null,
  type: 'totalevents',
  startDate: getTime(DEFAULT_ANALYTICS_PERIOD_VALUE.start),
  endDate: getTime(DEFAULT_ANALYTICS_PERIOD_VALUE.end),
  chart: [],
  loadingAnalyticsData: false,
  analyticsData: null,
  reviewsData: null,
  buildingFilters: [],
  packagesAnalytics: null,
  loadingPackagesAnalytics: false,
}

const buildingAnalyticsReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_LOADING_ANALYTICS_DATA:
      return {
        ...state,
        loadingAnalyticsData: action.loadingAnalyticsData,
      }

    case SET_BUILDING_STATISTICS:
      return {
        ...state,
        buildingStatistics:
          action.role === 'Administrator' && state.buildingFilters.length === 0
            ? null
            : action.buildingStatistics,
      }

    case SET_TYPE:
      return {
        ...state,
        type: action.value,
      }

    case SET_DATE:
      return {
        ...state,
        startDate: action.startDate,
        endDate: action.endDate,
      }

    case SET_CHART:
      return {
        ...state,
        chart:
          action.role === 'Administrator' && state.buildingFilters.length === 0 ? [] : action.chart,
      }

    case SET_ANALYTICS_DATA:
      return {
        ...state,
        analyticsData: cloneDeep(action.data), // protect from external mutation
      }

    case SET_REVIEWS_DATA:
      return {
        ...state,
        reviewsData: action.data,
      }

    case TOGGLE_BUILDING_FILTERS:
      const filterAlreadyExist = state.buildingFilters.find(
        (building) => building.id === action.building.id,
      )
      const buildingFilters = filterAlreadyExist
        ? state.buildingFilters.filter((building) => building.id !== action.building.id)
        : [...state.buildingFilters, action.building]

      return {
        ...state,
        buildingFilters,
        buildingStatistics: buildingFilters.length > 0 ? state.buildingStatistics : null,
        chart: buildingFilters.length > 0 ? state.chart : [],
      }

    case SELECT_ALL_BUILDINGS:
      return {
        ...state,
        buildingFilters:
          state.buildingFilters.length === action.buildings.length ? [] : action.buildings,
      }

    case SET_LOADING_PACKAGES_ANALYTICS:
      return {
        ...state,
        loadingPackagesAnalytics: action.loading,
      }

    case SET_PACKAGES_ANALYTICS:
      return {
        ...state,
        packagesAnalytics: action.data,
      }

    default:
      return state
  }
}

// Action Creators

const setLoadingAnalyticsData = (loadingAnalyticsData) => ({
  type: SET_LOADING_ANALYTICS_DATA,
  loadingAnalyticsData,
})

const setChart = (chart, role) => ({
  type: SET_CHART,
  chart,
  role,
})

const setAnalyticsData = (data) => ({
  type: SET_ANALYTICS_DATA,
  data: cloneDeep(data), // safe when called directly
})

const setReviewsData = (data) => ({
  type: SET_REVIEWS_DATA,
  data,
})

export const selectAllBuildings = (buildings) => ({
  type: SELECT_ALL_BUILDINGS,
  buildings,
})

export const setType = (value) => ({
  type: SET_TYPE,
  value,
})

export const setDate = (startDate, endDate) => ({
  type: SET_DATE,
  startDate,
  endDate,
})

export const toggleBuildingFilters = (building) => ({
  type: TOGGLE_BUILDING_FILTERS,
  building,
})

const setPackagesAnalytics = (data) => ({
  type: SET_PACKAGES_ANALYTICS,
  data,
})

const setLoadingPackagesAnalytics = (loading) => ({
  type: SET_LOADING_PACKAGES_ANALYTICS,
  loading,
})

// Utility
const getScaleForChart = (startDate, endDate) => {
  const differenceInDays = Math.ceil(
    Math.abs(moment.duration(moment(new Date(startDate)).diff(new Date(endDate))).asDays()),
  )
  return differenceInDays < 32 ? differenceInDays : 12
}

// Thunks

export const getBuildingAnalyticsChart = () => {
  return async (dispatch, getState) => {
    dispatch(setLoadingAnalyticsData(true))
    const { type, startDate, endDate, buildingFilters } = getState().buildingAnalytics
    const {
      user: { role },
    } = getState().userData

    const scale = getScaleForChart(startDate, endDate)

    if (role === 'PropertyManager' || (role === 'Administrator' && buildingFilters.length > 0)) {
      try {
        const res = await API.getBuildingAnalyticsChart(
          startDate,
          endDate,
          type,
          scale,
          role,
          buildingFilters,
        )
        dispatch(setChart(res.graph, role))
      } catch (err) {
        console.error('Error loading chart:', err)
      } finally {
        dispatch(setLoadingAnalyticsData(false))
      }
    }
  }
}

export const getBuildingAnalyticsDataAndChart = () => {
  return async (dispatch, getState) => {
    const { startDate, endDate } = getState().buildingAnalytics
    const selectedBuilding = getState().buildingSelector.activeBuilding

    if (selectedBuilding) {
      const formatStart = moment(new Date(startDate).setHours(0, 0, 0, 0))
        .tz(selectedBuilding.timezone, true)
        .valueOf()
      const formatEnd = moment(new Date(endDate).setHours(23, 59, 59, 59))
        .tz(selectedBuilding.timezone, true)
        .valueOf()

      try {
        const [reviewsRes, analyticsRes] = await Promise.all([
          API.getAnalyticsReviews(selectedBuilding.id),
          API.getAnalytics(formatStart, formatEnd, selectedBuilding.id),
        ])

        dispatch(setReviewsData(reviewsRes.data))
        dispatch(setAnalyticsData(analyticsRes.data)) // ✅ safe
      } catch (err) {
        console.error('Error fetching analytics data:', err)
      } finally {
        dispatch(setLoadingAnalyticsData(false))
      }
    }
  }
}

export const setLoadingAnalyticsDataState = (loadingAnalyticsData) => {
  return function (dispatch) {
    dispatch(setLoadingAnalyticsData(loadingAnalyticsData))
  }
}

export const getPackagesAnalytics = (buildingId) => {
  return async (dispatch) => {
    dispatch(setLoadingPackagesAnalytics(true))
    try {
      const res = await API.getPackagesAnalytics(buildingId)
      dispatch(setPackagesAnalytics(res.data))
    } catch (err) {
      console.error('Error fetching packages analytics:', err)
    } finally {
      dispatch(setLoadingPackagesAnalytics(false))
    }
  }
}

// Selectors
export const selectBuildingStatistics = (state) => state.buildingAnalytics.buildingStatistics
export const selectIsLoadingAnalytics = (state) => state.buildingAnalytics.loadingAnalyticsData
export const selectChart = (state) => state.buildingAnalytics.chart
export const selectType = (state) => state.buildingAnalytics.type
export const selectBuildingFilters = (state) => state.buildingAnalytics.buildingFilters
export const selectAnalyticsData = (state) => state.buildingAnalytics.analyticsData
export const selectReviewsData = (state) => state.buildingAnalytics.reviewsData
export const selectStartEndDates = (state) => [
  state.buildingAnalytics.startDate,
  state.buildingAnalytics.endDate,
]
export const selectPackagesAnalytics = (state) => state.buildingAnalytics.packagesAnalytics
export const selectLoadingPackagesAnalytics = (state) =>
  state.buildingAnalytics.loadingPackagesAnalytics

export default buildingAnalyticsReducer
